import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { staffApi } from '../services/api';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface Staff {
  id: string;
  storeId: string;
  name: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface StaffState {
  staff: Staff[];
  selectedStaff: Staff | null;
  isLoading: boolean;
  hasFetched: boolean; // 標記是否已完成過一次讀取
  error: string | null;
  showDeleteConfirmModal: boolean;
  staffToDeleteId: string | null;

  // Actions
  fetchStaff: (storeId: string) => Promise<void>;
  createStaff: (storeId: string, data: any) => Promise<void>;
  updateStaff: (id: string, data: any) => Promise<void>;
  toggleStaffStatus: (id: string, isActive: boolean) => Promise<void>;
  handleDelete: (id: string) => void;
  confirmDelete: () => Promise<void>;
  closeDeleteModal: () => void;
  setSelectedStaff: (staff: Staff | null) => void;
}

export const useStaffStore = create<StaffState>()(
  persist(
    (set, get) => ({
  staff: [],
  selectedStaff: null,
  isLoading: false,
  hasFetched: false,
  error: null,
  showDeleteConfirmModal: false,
  staffToDeleteId: null,

  fetchStaff: async (storeId: string) => {
    const { staff, hasFetched } = get();
    const hasExistingData = staff.length > 0;

    // 只有在首次讀取且無資料時才顯示載入狀態
    // hasFetched 確保「讀取後無資料」的情況不會重複顯示載入
    set({ isLoading: !hasExistingData && !hasFetched, error: null });

    try {
      const response = await staffApi.getByStore(storeId);
      set({ staff: response.data, isLoading: false, hasFetched: true });
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.fetchFailed');
      set({ error: errorMsg, isLoading: false, hasFetched: true });
      toast.error(errorMsg);
    }
  },

  createStaff: async (storeId: string, data: any) => {
    // 樂觀更新：立即新增臨時項目
    const tempId = `temp-${Date.now()}`;
    const optimisticStaff: Staff = {
      id: tempId,
      storeId,
      name: data.name,
      phone: data.phone,
      email: data.email,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    set((state) => ({
      staff: [...state.staff, optimisticStaff],
    }));
    toast.success(i18n.t('common.messages.createSuccess'));

    // 背景 API（不 await，讓 Dialog 立即關閉）
    staffApi.create(storeId, data)
      .then((response) => {
        set((state) => ({
          staff: state.staff.map((s) => s.id === tempId ? response.data : s),
        }));
      })
      .catch((error: any) => {
        set((state) => ({
          staff: state.staff.filter((s) => s.id !== tempId),
        }));
        const errorMsg = error.response?.data?.message || i18n.t('common.messages.createFailed');
        toast.error(errorMsg);
      });
  },

  updateStaff: async (id: string, data: any) => {
    // 1. 保存舊值
    const originalStaff = get().staff;

    // 2. 樂觀更新
    set((state) => ({
      staff: state.staff.map((s) => s.id === id ? { ...s, ...data } : s),
    }));

    try {
      const response = await staffApi.update(id, data);
      // 3. 用真實資料替換
      set((state) => ({
        staff: state.staff.map((s) => s.id === id ? response.data : s),
      }));
      toast.success(i18n.t('common.messages.updateSuccess'));
    } catch (error: any) {
      // 4. 失敗回滾
      set({ staff: originalStaff });
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
      toast.error(errorMsg);
      throw error;
    }
  },

  toggleStaffStatus: async (id: string, isActive: boolean) => {
    // 樂觀更新
    const previousStaff = get().staff;
    set((state) => ({
      staff: state.staff.map((s) =>
        s.id === id ? { ...s, isActive } : s
      ),
    }));

    try {
      await staffApi.toggleStatus(id, isActive);
      toast.success(i18n.t('common.messages.updateSuccess'));
    } catch (error: any) {
      // 失敗時回滾
      set({ staff: previousStaff });
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
      toast.error(errorMsg);
      throw error;
    }
  },

  handleDelete: (id: string) => {
    set({ showDeleteConfirmModal: true, staffToDeleteId: id });
  },

  confirmDelete: async () => {
    const { staffToDeleteId } = get();
    if (!staffToDeleteId) return;

    // 樂觀更新：立即關閉 Modal + 移除項目
    const previousStaff = get().staff;
    set((state) => ({
      staff: state.staff.filter((s) => s.id !== staffToDeleteId),
      showDeleteConfirmModal: false,
      staffToDeleteId: null,
    }));

    try {
      await staffApi.delete(staffToDeleteId);
      toast.success(i18n.t('common.messages.deleteSuccess'));
    } catch (error: any) {
      // 失敗時回滾
      set({ staff: previousStaff });
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.deleteFailed');
      toast.error(errorMsg);
    }
  },

  closeDeleteModal: () => {
    set({
      showDeleteConfirmModal: false,
      staffToDeleteId: null
    });
  },

  setSelectedStaff: (selectedStaff) => set({ selectedStaff }),
}),
    {
      name: 'staff-storage',
      partialize: (state) => ({
        staff: state.staff,
        hasFetched: state.hasFetched,
      }),
    }
  )
);
